import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import moment from "moment";
import styles from "./ScheduleLessonPlanContent.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton, CustomDatePicker } from "../../../../components";
import ScheduleTable from "../../../../components/Scheduletable/Scheduletable";
import PopupModal from "../../../../components/PopupModal/PopupModal";

/** Safely format a date to YYYY-MM-DD regardless of input type. */
const formatScheduleDate = (value) => {
  if (!value) return "";
  if (typeof value === "object" && typeof value.format === "function") {
    if (typeof value.isValid === "function" && !value.isValid()) return "";
    return value.format("YYYY-MM-DD");
  }
  return moment(value).format("YYYY-MM-DD");
};

/** Normalize subject/class strings for safe comparison. */
const normalize = (val) =>
  String(val || "")
    .trim()
    .toUpperCase();

const LESSON_STATUS_MAP = {
  0: "",
  1: "submitted",
  2: "approved",
  3: "rejected",
  4: "resubmitted",
  5: "completed",
};

/** Statuses that should NOT be selectable by the user. */
const NON_SELECTABLE_STATUSES = new Set([
  "submitted",
  "approved",
  "resubmitted",
  "completed",
]);

/** Human-readable labels for blocked statuses (used in error messages). */
const STATUS_LABELS = {
  submitted: "Submitted",
  approved: "Approved",
  resubmitted: "Re-Submitted",
  completed: "Completed",
};

const mapLessonStatus = (rawStatus) => {
  if (rawStatus === null || rawStatus === undefined || rawStatus === "") {
    return "";
  }
  const key = Number(rawStatus);
  if (Number.isNaN(key)) return "";
  return LESSON_STATUS_MAP[key] ?? "";
};

/** Transform API timetable → ScheduleTable schedule shape. */
const transformTimetableResponse = (timetableData) => {
  if (!timetableData || !Array.isArray(timetableData.timetable)) return [];
  return timetableData.timetable.map((dayRow) => ({
    day: dayRow.day_name,
    date: moment(dayRow.date).format("MMM - D"),
    rawDate: dayRow.date,
    periods: (dayRow.periods || []).map((p) => {
      const hasClass = p.class_name && p.subject_name;
      return {
        periodId: String(p.period),
        subject: hasClass ? p.subject_name : null,
        section: hasClass
          ? `${p.class_name}${p.section_id ? ` - ${p.section_id}` : ""}`
          : null,
        status: mapLessonStatus(p.status),
        class_id: p.class_id,
        class_name: p.class_name,
        section_id: p.section_id,
        subject_name: p.subject_name,
        course_id: p.course_id,
        period: p.period,
        period_id: p.id,
        hm_id: p.hm_id ?? null,
        medium: p.medium ?? "",
      };
    }),
  }));
};

const buildPeriodsFromTimetable = (timetableData) => {
  if (
    !timetableData ||
    !Array.isArray(timetableData.timetable) ||
    timetableData.timetable.length === 0
  ) {
    return [];
  }
  return (timetableData.timetable[0].periods || []).map((p) => ({
    id: String(p.period),
    label: String(p.period),
    time:
      p.break_start_time && p.break_end_time
        ? `${p.break_start_time} - ${p.break_end_time}`
        : "",
  }));
};

const hydrateSelectedCellsFromApi = (selectedPeriodsFromApi, timetableRows) => {
  if (!Array.isArray(selectedPeriodsFromApi) || !selectedPeriodsFromApi.length)
    return [];
  const cells = [];
  selectedPeriodsFromApi.forEach((sp) => {
    const apiDate = moment(sp.date).format("YYYY-MM-DD");
    const periodId = String(sp.period);

    const dayRow = timetableRows.find(
      (r) => moment(r.rawDate).format("YYYY-MM-DD") === apiDate,
    );
    if (!dayRow) return;

    const periodRow = dayRow.periods.find((p) => p.periodId === periodId);
    if (!periodRow) return;

    cells.push({
      date: dayRow.date,
      rawDate: dayRow.rawDate,
      periodId,
      subject: periodRow.subject_name || sp.subject || "",
      hm_id: periodRow.hm_id ?? sp.hm_id ?? null,
      rawPeriod: periodRow,
    });
  });
  return cells;
};

const ScheduleLessonPlanContent = ({
  lessonPlan,
  lessonInfo,
  onCancel,
  onSubmit,
}) => {
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser() || {};
  const clientData = user?.client_data?.[0] || {};
  const academic_id = clientData?.academic_year_id || "";
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;
  const userId = user.id != null ? Number(user.id) : null;
  const academic_start_date = clientData?.start_date || "";
  const academic_end_date = clientData?.end_date || "";

  const initialScheduleDate = useMemo(() => {
    const sd = lessonPlan?.schedule_date;
    if (sd) {
      const d = dayjs(sd);
      if (d.isValid()) return d;
    }
    return dayjs();
  }, [lessonPlan?.schedule_date]);

  const [scheduleDate, setScheduleDate] = useState(initialScheduleDate);
  const [timetableRows, setTimetableRows] = useState([]);
  const [timetablePeriods, setTimetablePeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [selectedCells, setSelectedCells] = useState([]);
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const [preselectApplied, setPreselectApplied] = useState(false);
  const lockedSubject =
    lessonPlan?.subject_name || lessonInfo?.subject_name || "";
  const lockedMedium = lessonPlan?.medium || lessonInfo?.medium || "";
  const lockedClassId = useMemo(() => {
    const id =
      lessonPlan?.class_id != null && lessonPlan?.class_id !== ""
        ? lessonPlan.class_id
        : lessonInfo?.class_id != null && lessonInfo?.class_id !== ""
          ? lessonInfo.class_id
          : null;
    return id != null ? String(id) : "";
  }, [lessonPlan?.class_id, lessonInfo?.class_id]);

  const lockedClassName =
    lessonPlan?.class_name || lessonInfo?.class || lessonInfo?.class_name || "";

  const legendItems = useMemo(
    () => [
      { key: "submitted", label: t.legendSubmitted || "Submitted" },
      { key: "approved", label: t.legendApproved || "Approved" },
      { key: "rejected", label: t.legendRejected || "Rejected" },
      { key: "resubmitted", label: t.legendResubmitted || "Re-Submitted" },
      { key: "completed", label: t.legendCompleted || "Completed" },
    ],
    [
      t.legendSubmitted,
      t.legendApproved,
      t.legendRejected,
      t.legendResubmitted,
      t.legendCompleted,
    ],
  );

  const selectedCellsForTable = useMemo(
    () =>
      selectedCells.map((c) => ({
        date: c.date,
        periodId: c.periodId,
      })),
    [selectedCells],
  );

  const fetchTimetable = useCallback(
    async (dateValue) => {
      if (!schoolId || !staffId || !dateValue) return;

      if (
        typeof dateValue === "object" &&
        typeof dateValue.isValid === "function" &&
        !dateValue.isValid()
      ) {
        return;
      }

      const formattedDate = formatScheduleDate(dateValue);
      if (!formattedDate) return;

      try {
        setLoading(true);
        setError("");
        const res = await AxiosInstance.post("/book/getStaffTimetable", {
          school_id: schoolId,
          staff_id: staffId,
          schedule_date: formattedDate,
          academic_year: academic_id,
        });

        if (res.data?.status && res.data.data) {
          const rows = transformTimetableResponse(res.data.data);
          setTimetableRows(rows);
          setTimetablePeriods(buildPeriodsFromTimetable(res.data.data));
          return rows;
        }
        setTimetableRows([]);
        setTimetablePeriods([]);
        setError(res.data?.message || t.noTimetableFound || "");
        return [];
      } catch (err) {
        console.error("Error fetching timetable:", err);
        setTimetableRows([]);
        setTimetablePeriods([]);
        setError(err?.response?.data?.message || t.somethingWentWrong || "");
        return [];
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId, staffId, t],
  );

  useEffect(() => {
    (async () => {
      const rows = await fetchTimetable(initialScheduleDate);
      if (
        !preselectApplied &&
        Array.isArray(lessonPlan?.selected_periods) &&
        lessonPlan.selected_periods.length > 0 &&
        Array.isArray(rows) &&
        rows.length > 0
      ) {
        const hydrated = hydrateSelectedCellsFromApi(
          lessonPlan.selected_periods,
          rows,
        );
        setSelectedCells(hydrated);
        setPreselectApplied(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScheduleDateChange = useCallback(
    (value) => {
      setScheduleDate(value);
      setSelectedCells([]);
      setPreselectApplied(true);
      if (value) {
        fetchTimetable(value);
      } else {
        setTimetableRows([]);
        setTimetablePeriods([]);
      }
    },
    [fetchTimetable],
  );

  const handleCellClick = useCallback(
    (cellInfo) => {
      const key = `${cellInfo.date}_${cellInfo.periodId}`;

      const alreadySelectedIndex = selectedCells.findIndex(
        (c) => `${c.date}_${c.periodId}` === key,
      );
      if (alreadySelectedIndex !== -1) {
        setSelectedCells((prev) =>
          prev.filter((_, i) => i !== alreadySelectedIndex),
        );
        return;
      }

      const cellDateStr = cellInfo.rawDate || cellInfo.date;
      const cellDay = moment(cellDateStr).startOf("day");
      const today = moment().startOf("day");
      if (cellDay.isBefore(today)) {
        setErrorModal({
          open: true,
          message:
            t.cannotSelectPastDate ||
            "You cannot select a past date. Please choose today or a future date.",
        });
        return;
      }

      const cellSubject =
        cellInfo.rawPeriod?.subject_name || cellInfo.subject || "";

      if (!cellSubject) {
        setErrorModal({
          open: true,
          message:
            t.invalidEmptyCell ||
            "This period has no subject assigned. Please select a valid period.",
        });
        return;
      }

      const cellStatus = String(cellInfo.rawPeriod?.status || "")
        .trim()
        .toLowerCase();
      if (NON_SELECTABLE_STATUSES.has(cellStatus)) {
        const statusLabel = STATUS_LABELS[cellStatus] || cellStatus;
        setErrorModal({
          open: true,
          message:
            t.cannotSelectStatusCell ||
            `This period is already "${statusLabel}" and cannot be selected. You can only select periods that are not yet submitted, or that have been rejected.`,
        });
        return;
      }

      if (
        lockedSubject &&
        normalize(cellSubject) !== normalize(lockedSubject)
      ) {
        setErrorModal({
          open: true,
          message:
            (t.invalidSubjectSelection ||
              "You can only select periods with the same subject") +
            ` (${lockedSubject}).`,
        });
        return;
      }

      // ────────────────────────────────────────────────────────────
      //  CLASS RESTRICTION
      // ────────────────────────────────────────────────────────────
      const cellClassId = cellInfo.rawPeriod?.class_id
        ? String(cellInfo.rawPeriod.class_id)
        : "";

      if (lockedClassId && cellClassId && cellClassId !== lockedClassId) {
        const cellClassName = cellInfo.rawPeriod?.class_name || cellClassId;
        setErrorModal({
          open: true,
          message:
            (t.invalidClassSelection ||
              "You can only select periods that match the selected class") +
            (lockedClassName
              ? ` (${lockedClassName}). This cell belongs to ${cellClassName}.`
              : "."),
        });
        return;
      }

      // ────────────────────────────────────────────────────────────
      //  MEDIUM RESTRICTION  (EM vs TA, etc.)
      // ────────────────────────────────────────────────────────────
      const cellMedium = cellInfo.rawPeriod?.medium || cellInfo.medium || "";
      const effectiveLockedMedium =
        lockedMedium ||
        selectedCells[0]?.rawPeriod?.medium ||
        selectedCells[0]?.medium ||
        "";

      if (
        effectiveLockedMedium &&
        cellMedium &&
        normalize(cellMedium) !== normalize(effectiveLockedMedium)
      ) {
        setErrorModal({
          open: true,
          message:
            (t.invalidMediumSelection ||
              "You can only select periods with the same medium") +
            ` (${effectiveLockedMedium}).`,
        });
        return;
      }

      if (selectedCells.length > 0) {
        const first = selectedCells[0];
        const firstSubject =
          first.rawPeriod?.subject_name || first.subject || "";

        if (
          firstSubject &&
          normalize(firstSubject) !== normalize(cellSubject)
        ) {
          setErrorModal({
            open: true,
            message:
              t.invalidSubjectSelection ||
              "You can only select periods with the same subject.",
          });
          return;
        }
      }

      const cellSectionId = cellInfo.rawPeriod?.section_id
        ? String(cellInfo.rawPeriod.section_id)
        : "";

      const duplicateCombo = selectedCells.some((c) => {
        const existingSubject = c.rawPeriod?.subject_name || c.subject || "";
        const existingClassId = c.rawPeriod?.class_id
          ? String(c.rawPeriod.class_id)
          : "";
        const existingSectionId = c.rawPeriod?.section_id
          ? String(c.rawPeriod.section_id)
          : "";
        return (
          normalize(existingSubject) === normalize(cellSubject) &&
          existingClassId === cellClassId &&
          existingSectionId === cellSectionId
        );
      });

      if (duplicateCombo) {
        const cellClassName = cellInfo.rawPeriod?.class_name || "";
        const comboLabel = `${cellSubject} ${cellClassName}${
          cellSectionId ? ` - ${cellSectionId}` : ""
        }`.trim();
        setErrorModal({
          open: true,
          message:
            t.duplicateSectionSelection ||
            `You have already selected ${comboLabel}. You can select a different section of the same class, but not the same section twice.`,
        });
        return;
      }

      const enrichedCell = {
        ...cellInfo,
        hm_id:
          cellInfo.rawPeriod?.hm_id != null
            ? cellInfo.rawPeriod.hm_id
            : (cellInfo.hm_id ?? null),
      };
      setSelectedCells((prev) => [...prev, enrichedCell]);
    },
    [
      selectedCells,
      lockedSubject,
      lockedClassId,
      lockedClassName,
      lockedMedium,
      t,
    ],
  );

  const closeErrorModal = useCallback(
    () => setErrorModal({ open: false, message: "" }),
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedCells.length) {
      setErrorModal({
        open: true,
        message:
          t.selectAtLeastOneCell ||
          "Please select at least one period to schedule.",
      });
      return;
    }

    const first = selectedCells[0];
    const classIdFromCell = first?.rawPeriod?.class_id
      ? Number(first.rawPeriod.class_id)
      : lessonPlan?.class_id || null;
    const courseIdFromCell = first?.rawPeriod?.course_id
      ? Number(first.rawPeriod.course_id)
      : null;

    const selected_periods = selectedCells.map((c) => ({
      date: c.rawDate || c.date,
      section_id: c.rawPeriod?.section_id || "",
      period: Number(c.rawPeriod?.period || c.periodId),
      hm_id: c.hm_id ?? c.rawPeriod?.hm_id ?? null,
    }));

    const payload = {
      id: selectedCells[0].hm_id ? selectedCells[0].hm_id : "",
      lesson_plan_id: lessonPlan?.lesson_plan_id || lessonPlan?.id,
      school_id: schoolId,
      staff_id: staffId,
      item_id: lessonPlan.id || "",
      version: lessonPlan?.version || 1,
      content: lessonPlan?.content || "",
      start_page: lessonPlan?.start_page,
      end_page: lessonPlan?.end_page,
      status: 1,
      class_id: classIdFromCell,
      course_id: courseIdFromCell,
      selected_periods,
      schedule_date: formatScheduleDate(scheduleDate),
      created_by: userId,
    };

    try {
      setSubmitting(true);
      const res = await AxiosInstance.post("/book/lessonPlan-save", payload);

      if (res.data?.status) {
        await onSubmit?.({
          lessonPlanId: lessonPlan?.id,
          scheduleDate: formatScheduleDate(scheduleDate),
          selectedCells,
          apiResponse: res.data,
        });
      } else {
        setErrorModal({
          open: true,
          message:
            res.data?.message ||
            t.failedToScheduleLessonPlan ||
            "Failed to schedule lesson plan",
        });
      }
    } catch (err) {
      console.error("Error scheduling lesson plan:", err);
      setErrorModal({
        open: true,
        message:
          err?.response?.data?.message ||
          t.somethingWentWrong ||
          "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCells,
    lessonPlan,
    lessonInfo,
    scheduleDate,
    schoolId,
    staffId,
    userId,
    onSubmit,
    t,
  ]);

  return (
    <>
      <PopupModal
        open={errorModal.open}
        title={t.modalFailedTitle || "Invalid Selection"}
        message={errorModal.message}
        onOk={closeErrorModal}
        onClose={closeErrorModal}
      />

      <div className={styles.wrapper}>
        <div className={styles.dateRow}>
          <div className={styles.dateField}>
            <CustomDatePicker
              label={t.scheduleDate || "Schedule Date"}
              name="scheduleDate"
              value={scheduleDate}
              onChange={handleScheduleDateChange}
              placeholder={t.selectDate || "Select Date"}
              required
              minDate={
                academic_start_date ? dayjs(academic_start_date) : undefined
              }
              maxDate={academic_end_date ? dayjs(academic_end_date) : undefined}
            />
          </div>
        </div>

        <div className={styles.scheduleWrap}>
          {loading ? (
            <div className={styles.emptyState}>
              {t.loadingTimetable || "Loading timetable..."}
            </div>
          ) : timetableRows.length > 0 ? (
            <ScheduleTable
              periods={timetablePeriods}
              schedule={timetableRows}
              showLegend
              legendItems={legendItems}
              selectable
              selectedCells={selectedCellsForTable}
              onCellClick={handleCellClick}
            />
          ) : (
            <div className={styles.emptyState}>
              {error ||
                t.noTimetableFound ||
                "No timetable found for this week."}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.cancelButton}
            fullWidth={false}
            onClick={onCancel}
          >
            {t.cancelButton || "Cancel"}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.submitButton}
            fullWidth={false}
            onClick={handleSubmit}
            disabled={submitting || !selectedCells.length}
          >
            {t.submitToHM || "Submit To HM"}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default ScheduleLessonPlanContent;
